import { create } from "zustand"
import {
  DEFAULT_FILTERS,
  RANGE_PRESETS,
  resolveAutoGranularity,
  type GlobalFilters,
  type RangePreset,
} from "@csbi/contracts"

/**
 * The global filter store. It is **a mirror of the URL**, not the source of truth.
 *
 * Server data always goes through react-query and never into zustand — two caches side by side breed bugs.
 * All that lives here is "what the user currently has selected".
 */

export type FilterStore = {
  filters: GlobalFilters
  /** Do not write back to the URL before the first read from it, or the defaults would overwrite the link's parameters */
  hydrated: boolean
  set: (patch: Partial<GlobalFilters>) => void
  replace: (next: GlobalFilters) => void
  hydrate: (next: GlobalFilters) => void
  reset: () => void
}

export const useFilterStore = create<FilterStore>((set) => ({
  filters: DEFAULT_FILTERS,
  hydrated: false,
  set: (patch) => set((s) => ({ filters: { ...s.filters, ...patch } })),
  replace: (next) => set({ filters: next }),
  hydrate: (next) => set({ filters: next, hydrated: true }),
  reset: () => set({ filters: DEFAULT_FILTERS }),
}))

export const RANGE_PRESET_LABELS: Record<RangePreset, string> = {
  today: "Today",
  last7d: "Last 7 days",
  last30d: "Last 30 days",
  last90d: "Last 90 days",
  mtd: "Month to date",
  qtd: "Quarter to date",
  ytd: "Year to date",
}

export const RANGE_PRESET_ORDER = RANGE_PRESETS

/** Days per preset, used to resolve `granularity: "auto"` into a sub-label ("Auto (weekly)") */
const PRESET_DAYS: Record<RangePreset, number> = {
  today: 1,
  last7d: 7,
  last30d: 30,
  last90d: 90,
  mtd: 30,
  qtd: 91,
  ytd: 365,
}

export function spanDaysOf(filters: GlobalFilters): number {
  if ("preset" in filters.range) return PRESET_DAYS[filters.range.preset]
  const ms = new Date(filters.range.to).getTime() - new Date(filters.range.from).getTime()
  return Math.max(1, Math.round(ms / 86_400_000))
}

/** The actual granularity shown in the UI. `auto` has to state what it resolved to, or the reader does not know how long one point covers */
export function effectiveGranularity(filters: GlobalFilters): "day" | "week" | "month" {
  return filters.granularity === "auto" ? resolveAutoGranularity(spanDaysOf(filters)) : filters.granularity
}

export function rangeLabel(filters: GlobalFilters): string {
  if ("preset" in filters.range) return RANGE_PRESET_LABELS[filters.range.preset]
  return `${filters.range.from.slice(0, 10)} ~ ${filters.range.to.slice(0, 10)}`
}
